/**
 * Intake — the photos the wardrobe was catalogued from, and whether anything got missed.
 *
 * This screen exists because of one afternoon. Brian noticed two denim shirts were not in the
 * wardrobe and asked "so i question what else is missing" — a fair question with no way to answer
 * it. `wardrobe_photos` had zero rows and no garment carried a `source_photo_id`, so there was
 * nothing to reconcile against. The answer turned out to be an entire photo of hanging jackets
 * that had never been processed: roughly a dozen garments, including the outer layer the shopping
 * list had spent days telling him to go and buy.
 *
 * The mechanism is deliberately manual in one place. `Items in this photo` is HIS count of what he
 * can see in the picture; `linked` is what the database actually holds. If that number were
 * derived it would always agree with itself and this screen would be decoration. The disagreement
 * IS the feature.
 */
import { useState } from 'react';
import { api } from '../../lib/api';
import type { WardrobePhoto } from '../../shared/types';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Loading,
  PanelHeading,
  TextInput,
  cx,
  errorText,
  useResource,
} from './ui';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 8 * 1024 * 1024;

export default function IntakePanel() {
  const { data, loading, error, reload, setData } = useResource<WardrobePhoto[]>(
    () => api.get<WardrobePhoto[]>('/wardrobe/photos'),
    []
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setProblem(null);
    setBusy(true);
    try {
      // Sequential rather than parallel: a folder drop of twenty phone photos would otherwise
      // open twenty concurrent 8MB uploads.
      for (const file of Array.from(files)) {
        if (!ACCEPTED.includes(file.type)) {
          setProblem(`${file.name} is not a JPEG, PNG or WebP.`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          setProblem(`${file.name} is over 8MB.`);
          continue;
        }
        const form = new FormData();
        form.append('photo', file);
        form.append('caption', file.name.replace(/\.[^.]+$/, ''));
        await api.upload<WardrobePhoto>('/wardrobe/photos', form);
      }
      reload();
    } catch (err) {
      setProblem(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function patch(updated: WardrobePhoto) {
    setData((cur) => (cur ? cur.map((p) => (p.id === updated.id ? updated : p)) : cur));
  }

  const photos = data ?? [];
  const unprocessed = photos.filter((p) => p.processed_at === null).length;
  const mismatched = photos.filter((p) => !p.reconciled).length;

  return (
    <section className="space-y-5">
      <PanelHeading
        title="Intake"
        sub="The photos the wardrobe was built from, and what came out of each one."
      />

      <Card tone="sunk">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-prose text-sm text-ink-soft">
            One photo can hold a dozen garments. Recording what you saw in each picture is the
            only way to notice when something never made it into the wardrobe.
          </p>
          <label>
            <input
              type="file"
              multiple
              accept={ACCEPTED.join(',')}
              className="sr-only"
              disabled={busy}
              onChange={(e) => void upload(e.target.files)}
            />
            <span
              className={cx(
                'inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full border px-4',
                'text-sm font-medium transition-colors',
                busy
                  ? 'border-border bg-surface text-ink-soft opacity-50'
                  : 'border-sage bg-sage text-white hover:bg-sage/90'
              )}
            >
              {busy ? 'Uploading…' : 'Add photos'}
            </span>
          </label>
        </div>
      </Card>

      {problem && (
        <Card tone="over">
          <p className="text-sm text-ink">{problem}</p>
        </Card>
      )}

      {loading && <Loading label="Loading intake photos" rows={3} />}
      {error && !loading && <ErrorState message={error} onRetry={reload} />}

      {data && !loading && !error && photos.length === 0 && (
        <EmptyState title="No intake photos recorded">
          The 47 garments already in the wardrobe were catalogued before this existed, so none of
          them are attributed yet. Anything added from here on can be.
        </EmptyState>
      )}

      {photos.length > 0 && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-display text-lg font-semibold text-ink">
              {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
            </span>
            {unprocessed > 0 && (
              <Chip tone="near">{unprocessed} not gone through yet</Chip>
            )}
            {mismatched > 0 && <Chip tone="over">{mismatched} with items missing</Chip>}
            {unprocessed === 0 && mismatched === 0 && (
              <Chip tone="ok">everything reconciles</Chip>
            )}
          </div>

          <ul className="grid gap-3 sm:grid-cols-2">
            {photos.map((p) => (
              <PhotoCard key={p.id} photo={p} onSaved={patch} onRemoved={reload} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function PhotoCard({
  photo,
  onSaved,
  onRemoved,
}: {
  photo: WardrobePhoto;
  onSaved: (p: WardrobePhoto) => void;
  onRemoved: () => void;
}) {
  const [caption, setCaption] = useState(photo.caption ?? '');
  const [found, setFound] = useState(photo.items_found === null ? '' : String(photo.items_found));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  async function save(markProcessed: boolean) {
    setErr(null);
    setSaving(true);
    try {
      const updated = await api.put<WardrobePhoto>(`/wardrobe/photos/${photo.id}`, {
        caption: caption.trim() === '' ? null : caption.trim(),
        items_found: found.trim() === '' ? null : Number(found),
        processed: markProcessed,
      });
      onSaved(updated);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    await api.del(`/wardrobe/photos/${photo.id}`).catch(() => {});
    onRemoved();
  }

  const missing =
    photo.items_found !== null ? photo.items_found - photo.linked_items : 0;

  return (
    <li>
      <Card className="flex h-full flex-col gap-3">
        <div className="flex gap-3">
          <span className="block h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-surface-sunk">
            {broken ? (
              <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-ink-soft">
                image missing
              </span>
            ) : (
              <img
                src={`/api/wardrobe/photos/${photo.id}/photo`}
                alt={photo.caption ?? `Intake photo ${photo.id}`}
                loading="lazy"
                onError={() => setBroken(true)}
                className="h-full w-full object-cover"
              />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {photo.processed_at === null ? (
                <Chip tone="near">Not gone through</Chip>
              ) : missing > 0 ? (
                <Chip tone="over">
                  {missing} missing
                </Chip>
              ) : (
                <Chip tone="ok">Done</Chip>
              )}
              <span className="text-xs text-ink-soft">
                {photo.linked_items} in the wardrobe
                {photo.items_found !== null && ` of ${photo.items_found}`}
              </span>
            </div>
            <p className="mt-1.5 text-xs text-ink-soft">
              {photo.uploaded_at.slice(0, 10)}
            </p>
          </div>
        </div>

        {err && (
          <p role="alert" className="rounded-lg border border-clay/40 bg-clay-soft px-2.5 py-1.5 text-sm text-ink">
            {err}
          </p>
        )}

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink">What this photo is</span>
          <TextInput
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="jackets on the rail"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink">
            Garments you can count in it
          </span>
          <TextInput
            value={found}
            onChange={(e) => setFound(e.target.value)}
            inputMode="numeric"
            placeholder="12"
          />
          <span className="mt-1 block text-xs text-ink-soft">
            Your count, not a calculated one — that is what makes a mismatch visible.
          </span>
        </label>

        <div className="mt-auto flex flex-wrap gap-2 pt-1">
          <Button variant="primary" onClick={() => void save(true)} disabled={saving}>
            {saving ? 'Saving…' : photo.processed_at ? 'Save' : 'Save and mark done'}
          </Button>
          {photo.processed_at && (
            <Button onClick={() => void save(false)} disabled={saving}>
              Reopen
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={remove} disabled={saving}>
            Remove
          </Button>
        </div>
      </Card>
    </li>
  );
}
